/**
 * Versioned, JSON-safe client/server clock-sync shadow measurements.
 *
 * t1/t4 are client Date.now() epoch milliseconds and t2/t3 are server epoch
 * milliseconds. They are observations only: wall-clock jumps are retained in
 * the measurement and never fed into the audio timeline. The capture anchor is
 * a pair of adjacent performance.now()-style monotonic and Date.now() epoch
 * milliseconds. The monotonic value can only be used with the sample offset and
 * canonical sample rate supplied beside it; the epoch value is diagnostic.
 */

export const CLOCK_SYNC_SCHEMA_VERSION = 1;
export const CLOCK_SYNC_MAX_EPOCH_MS = 10 ** 15;
export const CLOCK_SYNC_MAX_MONOTONIC_MS = 10 ** 12;
export const CLOCK_SYNC_MAX_SAMPLE_OFFSET = 10 ** 12;
export const CLOCK_SYNC_MAX_SAMPLE_RATE_HZ = 192_000;
export const CLOCK_SYNC_MAX_UNCERTAINTY_MS = 60_000;

const REQUIRED_FIELDS = [
  "version",
  "t1_client_send_ms",
  "t2_server_receive_ms",
  "t3_server_send_ms",
  "t4_client_receive_ms",
  "capture_anchor_monotonic_ms",
  "capture_anchor_epoch_ms",
  "sample_offset",
  "sample_rate_hz",
  "uncertainty_ms",
];
const PROBE_FIELDS = [
  "version",
  "t1_client_send_ms",
  "capture_anchor_monotonic_ms",
  "capture_anchor_epoch_ms",
  "sample_offset",
  "sample_rate_hz",
  "uncertainty_ms",
];

function finite(value, name) {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new TypeError(`${name} must be a finite number`);
  }
  return value;
}

function bounded(value, name, max) {
  finite(value, name);
  if (value < 0 || value > max) throw new RangeError(`${name} is outside its bound`);
  return value;
}

function integer(value, name, max) {
  bounded(value, name, max);
  if (!Number.isInteger(value)) throw new TypeError(`${name} must be an integer`);
  return value;
}

function positiveInteger(value, name, max) {
  const number = integer(value, name, max);
  if (number <= 0) throw new RangeError(`${name} must be positive`);
  return number;
}

function exactObject(value, fields, description) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new TypeError(`${description} must be an object`);
  }
  const keys = Object.keys(value).sort();
  const expected = [...fields].sort();
  if (keys.length !== expected.length || keys.some((key, i) => key !== expected[i])) {
    throw new TypeError(`${description} has missing or unknown fields`);
  }
  return value;
}

/** Validate the client request before it can be JSON-stringified or posted. */
export function validateClockSyncProbeRequest(value) {
  const object = exactObject(value, PROBE_FIELDS, "clock-sync probe");
  if (object.version !== CLOCK_SYNC_SCHEMA_VERSION) {
    throw new RangeError(`unsupported clock-sync version ${String(object.version)}`);
  }
  return {
    version: CLOCK_SYNC_SCHEMA_VERSION,
    t1_client_send_ms: bounded(object.t1_client_send_ms, "t1_client_send_ms", CLOCK_SYNC_MAX_EPOCH_MS),
    capture_anchor_monotonic_ms: bounded(
      object.capture_anchor_monotonic_ms,
      "capture_anchor_monotonic_ms",
      CLOCK_SYNC_MAX_MONOTONIC_MS
    ),
    capture_anchor_epoch_ms: bounded(
      object.capture_anchor_epoch_ms,
      "capture_anchor_epoch_ms",
      CLOCK_SYNC_MAX_EPOCH_MS
    ),
    sample_offset: integer(object.sample_offset, "sample_offset", CLOCK_SYNC_MAX_SAMPLE_OFFSET),
    sample_rate_hz: positiveInteger(object.sample_rate_hz, "sample_rate_hz", CLOCK_SYNC_MAX_SAMPLE_RATE_HZ),
    uncertainty_ms: bounded(object.uncertainty_ms, "uncertainty_ms", CLOCK_SYNC_MAX_UNCERTAINTY_MS),
  };
}

/** Validate and return a fresh measurement with no non-JSON numeric values. */
export function validateClockSyncMeasurement(value) {
  const object = exactObject(value, REQUIRED_FIELDS, "clock-sync measurement");
  if (object.version !== CLOCK_SYNC_SCHEMA_VERSION) {
    throw new RangeError(`unsupported clock-sync version ${String(object.version)}`);
  }
  const measurement = {
    version: CLOCK_SYNC_SCHEMA_VERSION,
    t1_client_send_ms: bounded(object.t1_client_send_ms, "t1_client_send_ms", CLOCK_SYNC_MAX_EPOCH_MS),
    t2_server_receive_ms: bounded(object.t2_server_receive_ms, "t2_server_receive_ms", CLOCK_SYNC_MAX_EPOCH_MS),
    t3_server_send_ms: bounded(object.t3_server_send_ms, "t3_server_send_ms", CLOCK_SYNC_MAX_EPOCH_MS),
    t4_client_receive_ms: bounded(object.t4_client_receive_ms, "t4_client_receive_ms", CLOCK_SYNC_MAX_EPOCH_MS),
    capture_anchor_monotonic_ms: bounded(
      object.capture_anchor_monotonic_ms,
      "capture_anchor_monotonic_ms",
      CLOCK_SYNC_MAX_MONOTONIC_MS
    ),
    capture_anchor_epoch_ms: bounded(
      object.capture_anchor_epoch_ms,
      "capture_anchor_epoch_ms",
      CLOCK_SYNC_MAX_EPOCH_MS
    ),
    sample_offset: integer(object.sample_offset, "sample_offset", CLOCK_SYNC_MAX_SAMPLE_OFFSET),
    sample_rate_hz: positiveInteger(object.sample_rate_hz, "sample_rate_hz", CLOCK_SYNC_MAX_SAMPLE_RATE_HZ),
    uncertainty_ms: bounded(object.uncertainty_ms, "uncertainty_ms", CLOCK_SYNC_MAX_UNCERTAINTY_MS),
  };
  // JSON.stringify permits NaN/Infinity as null. Validate once more at the
  // serialization boundary so a future field addition cannot weaken this rule.
  for (const [name, number] of Object.entries(measurement)) {
    if (name !== "version") finite(number, name);
  }
  return measurement;
}

/** Serialize only a validated measurement; never emit JSON null for a number. */
export function serializeClockSyncMeasurement(value) {
  return JSON.stringify(validateClockSyncMeasurement(value));
}

/** Return a sample mapping whose result cannot move backwards. */
export function mapMonotonicSample(
  anchorMonotonicMs,
  sampleOffset,
  sampleRateHz,
  monotonicNowMs
) {
  bounded(anchorMonotonicMs, "anchorMonotonicMs", CLOCK_SYNC_MAX_MONOTONIC_MS);
  integer(sampleOffset, "sampleOffset", CLOCK_SYNC_MAX_SAMPLE_OFFSET);
  positiveInteger(sampleRateHz, "sampleRateHz", CLOCK_SYNC_MAX_SAMPLE_RATE_HZ);
  bounded(monotonicNowMs, "monotonicNowMs", CLOCK_SYNC_MAX_MONOTONIC_MS);
  const elapsedMs = Math.max(0, monotonicNowMs - anchorMonotonicMs);
  const mapped = sampleOffset + Math.round((elapsedMs * sampleRateHz) / 1000);
  if (mapped > CLOCK_SYNC_MAX_SAMPLE_OFFSET) {
    throw new RangeError("mapped sample offset is outside its bound");
  }
  return mapped;
}

/** Subtract server processing time from the round trip when both wall clocks behaved. */
export function networkRttMs(measurement) {
  const value = validateClockSyncMeasurement(measurement);
  const clientElapsed = value.t4_client_receive_ms - value.t1_client_send_ms;
  const serverElapsed = value.t3_server_send_ms - value.t2_server_receive_ms;
  if (clientElapsed < 0 || serverElapsed < 0) return null;
  const network = clientElapsed - serverElapsed;
  return network >= 0 ? network : null;
}
